import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Home, Camera, CheckCircle2, Circle, RefreshCw, PenLine, X, Eraser, FileText, CalendarDays, Pencil } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { EdlDispute } from "../components/dashboard/EdlDispute";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PARTY_LABEL = { owner: "Bailleur", tenant: "Locataire", referent: "Référent" };
const SigDot = ({ ok, label }) => (
  <span className="inline-flex items-center gap-1 text-xs">{ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-brand-soft" />}{label}</span>
);

function SignModal({ token, party, defaultName, onClose, onDone }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [name, setName] = useState(defaultName || "");
  const [busy, setBusy] = useState(false);
  const pos = (e) => { const c = canvasRef.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#1f3d2b"; ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault(); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const submit = async () => {
    if (!name.trim()) { toast.error("Indiquez votre nom"); return; }
    const data = canvasRef.current.toDataURL("image/png");
    setBusy(true);
    try { const r = await api.post(`/edl/public/view/${token}/sign`, { party, signer_name: name, signature_png_b64: data }); toast.success("Signature enregistrée"); onDone(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="edlview-sign-modal">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-semibold text-brand-greenDeep">Signer · {PARTY_LABEL[party]}</h3><button onClick={onClose}><X className="w-5 h-5 text-brand-green/50" /></button></div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom complet" className="w-full px-3 py-2 rounded-xl border border-brand-soft mb-3 text-sm outline-none focus:border-brand-lime" data-testid="edlview-sign-name" />
        <canvas ref={canvasRef} width={380} height={150} className="w-full border border-brand-soft rounded-xl touch-none bg-brand-softer" onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="edlview-sign-canvas" />
        <div className="flex justify-between items-center mt-3">
          <button onClick={clear} className="inline-flex items-center gap-1 text-sm text-brand-green/60"><Eraser className="w-4 h-4" /> Effacer</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="edlview-sign-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Valider ma signature</button>
        </div>
      </div>
    </div>
  );
}

function CorrectionModal({ token, defaultParty, onClose, onDone }) {
  const [party, setParty] = useState(defaultParty || "tenant");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Décrivez la correction souhaitée"); return; }
    setBusy(true);
    try {
      await api.post(`/edl/public/view/${token}/request-correction`, { party, reason, requester_name: name });
      toast.success("Demande de correction transmise au référent");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="correction-modal">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-semibold text-brand-greenDeep">Demander une correction</h3><button onClick={onClose}><X className="w-5 h-5 text-brand-green/50" /></button></div>
        <p className="text-xs text-brand-green/55 mb-3">Votre demande sera transmise au référent, qui pourra corriger l'état des lieux avant une nouvelle signature de toutes les parties.</p>
        <label className="block text-xs font-semibold text-brand-green/70 mb-1">Je suis</label>
        <div className="flex gap-2 mb-3">
          {[["tenant", "Locataire"], ["owner", "Propriétaire"]].map(([v, l]) => (
            <button key={v} onClick={() => setParty(v)} className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold border ${party === v ? "bg-brand-greenDeep text-white border-brand-greenDeep" : "border-brand-soft text-brand-greenDeep"}`} data-testid={`corr-party-${v}`}>{l}</button>
          ))}
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom (optionnel)" className="w-full px-3 py-2 rounded-xl border border-brand-soft mb-2 text-sm outline-none focus:border-brand-lime" data-testid="corr-name" />
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="Décrivez la correction souhaitée (pièce, relevé, erreur…)" className="w-full px-3 py-2 rounded-xl border border-brand-soft mb-3 text-sm outline-none focus:border-brand-lime" data-testid="corr-reason" />
        <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="corr-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />} Envoyer la demande</button>
      </div>
    </div>
  );
}

export default function PublicEdlView() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [state, setState] = useState("loading");
  const [tick, setTick] = useState(0);
  const [signParty, setSignParty] = useState(null);
  const [corrParty, setCorrParty] = useState(null);
  const [ack, setAck] = useState(null);

  const load = () => api.get(`/edl/public/view/${token}`).then((r) => { setD(r.data); setState("ok"); }).catch(() => setState("invalid"));
  useEffect(() => { load(); const t = setInterval(() => { load(); setTick((x) => x + 1); }, 10000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [token]);

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center bg-brand-softer"><Loader2 className="w-7 h-7 animate-spin text-brand-green/40" /></div>;
  if (state === "invalid") return <div className="min-h-screen flex items-center justify-center bg-brand-softer text-brand-green/60" data-testid="edlview-invalid">Lien de consultation invalide ou expiré.</div>;

  const kind = d.type === "sortie" ? "sortie" : "entrée";
  return (
    <div className="min-h-screen bg-brand-softer py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-5" data-testid="public-edl-view">
        <div className="bg-white rounded-3xl border border-brand-soft p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Home className="w-5 h-5" /></div>
              <div>
                <h1 className="font-display text-xl font-semibold text-brand-greenDeep">État des lieux d'{kind}</h1>
                <p className="text-sm text-brand-green/55">{d.property_name} · {d.edl_date}</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-[11px] text-brand-green/40" data-testid="edlview-live"><RefreshCw className="w-3 h-3" /> Vue partagée · actualisation auto</span>
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-2 text-sm text-brand-green/70">
            <div><span className="text-brand-green/45">Bailleur :</span> {d.owner_name || "—"}</div>
            <div><span className="text-brand-green/45">Locataire :</span> {d.tenant_name || "—"}</div>
            <div><span className="text-brand-green/45">Référent :</span> {d.referent_name || "—"}</div>
          </div>
          <div className="mt-3 flex items-center gap-4 border-t border-brand-soft pt-3 flex-wrap">
            <span className="text-xs text-brand-green/45">Signatures :</span>
            <SigDot ok={d.signatures_status?.owner} label="Bailleur" />
            <SigDot ok={d.signatures_status?.tenant} label="Locataire" />
            <SigDot ok={d.signatures_status?.referent} label="Référent" />
          </div>
          <div className="mt-3 border-t border-brand-soft pt-3" data-testid="edlview-sign-stepper">
            <span className="text-xs text-brand-green/45">Signatures — ordre imposé :</span>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {["referent", "tenant", "owner"].map((p, i) => {
                const ok = d.signatures_status?.[p];
                const isNext = d.next_signer === p;
                return (
                  <div key={p} className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${ok ? "bg-emerald-100 text-emerald-700" : isNext ? "bg-brand-lime text-brand-greenDeep ring-2 ring-brand-greenDeep" : "bg-brand-soft text-brand-green/50"}`} data-testid={`step-${p}`}>
                      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {i + 1}. {PARTY_LABEL[p]}{isNext ? " — à signer" : ""}
                    </span>
                    {i < 2 && <span className="text-brand-green/30">→</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="edlview-sign-actions">
            {d.next_signer ? (
              <button onClick={() => setSignParty(d.next_signer)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid={`edlview-sign-${d.next_signer}`}>
                <PenLine className="w-4 h-4" /> Signer en tant que {PARTY_LABEL[d.next_signer]}
              </button>
            ) : (
              <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1" data-testid="edlview-all-signed"><CheckCircle2 className="w-4 h-4" /> État des lieux signé par toutes les parties</span>
            )}
            {d.next_signer && d.next_signer !== "referent" && (
              <span className="text-[11px] text-brand-green/45 self-center">En attente de la signature {d.next_signer === "owner" ? "du locataire" : "du référent"} avant celle-ci.</span>
            )}
            <button onClick={() => setCorrParty("tenant")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-gold/50 text-brand-limeDark hover:bg-brand-gold/10 text-sm font-semibold" data-testid="edlview-request-correction-btn">
              <Pencil className="w-4 h-4" /> Demander une correction
            </button>
          </div>
          {d.correction_requested && (
            <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800" data-testid="edlview-correction-banner">
              <b>Correction demandée</b> par {d.correction_requested.requester_name || (d.correction_requested.party === "owner" ? "le propriétaire" : "le locataire")} — en attente de traitement par le référent.
              <span className="block text-xs mt-0.5 text-amber-700/80">« {d.correction_requested.reason} »</span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-brand-soft p-6">
          <h2 className="font-display text-lg font-semibold text-brand-greenDeep mb-3">État des pièces</h2>
          <div className="space-y-3" data-testid="edlview-rooms">
            {(d.rooms || []).map((r, i) => (
              <div key={i} className="rounded-2xl border border-brand-soft p-3">
                <div className="flex items-center justify-between"><span className="font-semibold text-brand-greenDeep">{r.name}</span>{r.condition && <span className="text-xs px-2 py-0.5 rounded-full bg-brand-softer text-brand-green/70">{r.condition}</span>}</div>
                {(r.sols || r.murs || r.plafond) && <p className="text-xs text-brand-green/60 mt-1">Sols/Murs/Plafond : {[r.sols, r.murs, r.plafond].filter(Boolean).join(" / ")}</p>}
                {r.equipment && <p className="text-xs text-brand-green/60">Équipements : {r.equipment}</p>}
                {r.observations && <p className="text-xs text-brand-green/60">Obs. : {r.observations}</p>}
                {(r.photos || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(r.photos || []).slice(0, 8).map((ph, pi) => <img key={pi} src={`${BACKEND}${ph.url}`} alt="" className="w-16 h-16 object-cover rounded-lg border border-brand-soft" />)}
                  </div>
                )}
              </div>
            ))}
          </div>
          {d.general_observations && <div className="mt-4"><p className="text-sm font-semibold text-brand-greenDeep">Observations générales</p><p className="text-sm text-brand-green/70 whitespace-pre-wrap">{d.general_observations}</p></div>}
        </div>

        {d.comparison && (
          <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="edlview-comparison">
            <h2 className="font-display text-lg font-semibold text-brand-greenDeep mb-2">Comparatif entrée / sortie</h2>
            <p className="text-sm text-brand-green/70">{d.comparison.summary}</p>
            <div className="mt-2 space-y-1">
              {(d.comparison.differences || []).map((df, i) => (
                <div key={i} className="text-xs text-brand-green/70 border-l-2 border-brand-lime pl-2"><b>{df.room}</b> — {df.change} <span className="text-brand-green/45">({df.severity} · {df.responsibility})</span></div>
              ))}
            </div>
          </div>
        )}

        {/* Compteurs, clés & inventaire */}
        {(d.meters && Object.values(d.meters).some(Boolean)) || (d.keys && Object.values(d.keys).some(Boolean)) || (d.inventory || []).length > 0 ? (
          <div className="bg-white rounded-3xl border border-brand-soft p-6 space-y-4" data-testid="edlview-annex-info">
            {d.meters && Object.values(d.meters).some(Boolean) && (
              <div>
                <h2 className="font-display text-lg font-semibold text-brand-greenDeep mb-2">Relevé des compteurs</h2>
                <div className="grid sm:grid-cols-2 gap-2 text-sm text-brand-green/70">
                  {[["Électricité", d.meters.electricite], ["Gaz", d.meters.gaz], ["Eau froide", d.meters.eau_froide], ["Eau chaude", d.meters.eau_chaude]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l}><span className="text-brand-green/45">{l} :</span> {v}</div>
                  ))}
                </div>
              </div>
            )}
            {d.keys && Object.values(d.keys).some(Boolean) && (
              <div>
                <h3 className="text-sm font-semibold text-brand-greenDeep">Clés remises</h3>
                <p className="text-sm text-brand-green/70">Clés : {d.keys.cles || "—"} · Badges : {d.keys.badges || "—"} · Boîte aux lettres : {d.keys.boite_lettres || "—"}</p>
              </div>
            )}
            {(d.inventory || []).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-brand-greenDeep mb-1">Inventaire du mobilier</h3>
                <div className="space-y-1">
                  {(d.inventory || []).map((it, i) => (
                    <div key={i} className="text-xs text-brand-green/70">• {it.item} {it.room ? `(${it.room})` : ""} — Qté {it.quantity || 1}{it.condition ? ` · ${it.condition}` : ""}{it.observations ? ` · ${it.observations}` : ""}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Proposition de frais de remise en état (grille de vétusté + parts) */}
        {d.cost_allocation && (d.cost_allocation.items || []).length > 0 && (
          <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="edlview-cost-allocation">
            <h2 className="font-display text-lg font-semibold text-brand-greenDeep mb-1">Proposition de frais de remise en état du logement</h2>
            <p className="text-xs text-brand-green/55 mb-4">Chaque poste tient compte de la vétusté (usure normale) ; le solde reste à la charge du locataire.</p>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="text-brand-green/55 border-b border-brand-soft">
                  <tr>
                    <th className="text-left py-2 px-2 font-semibold">Élément</th>
                    <th className="text-left py-2 px-2 font-semibold">Poste</th>
                    <th className="text-right py-2 px-2 font-semibold">Coût</th>
                    <th className="text-right py-2 px-2 font-semibold">Vétusté</th>
                    <th className="text-right py-2 px-2 font-semibold">Part locataire</th>
                    <th className="text-right py-2 px-2 font-semibold">Part propriétaire</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.cost_allocation.items || []).map((it, i) => (
                    <tr key={i} className="border-b border-brand-soft/60" data-testid={`edlview-cost-row-${i}`}>
                      <td className="py-2 px-2 text-brand-greenDeep">{it.label || "—"}</td>
                      <td className="py-2 px-2 text-brand-green/60">{it.category}</td>
                      <td className="py-2 px-2 text-right text-brand-green/70">{Number(it.repair_cost || 0).toFixed(2)} €</td>
                      <td className="py-2 px-2 text-right text-brand-green/60">{it.vetuste_pct || 0} %</td>
                      <td className="py-2 px-2 text-right font-semibold text-brand-greenDeep">{Number(it.tenant_share || 0).toFixed(2)} €</td>
                      <td className="py-2 px-2 text-right text-brand-green/70">{Number(it.owner_share || 0).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-brand-softer font-semibold text-brand-greenDeep">
                    <td className="py-2 px-2" colSpan={2}>TOTAL</td>
                    <td className="py-2 px-2 text-right">{Number(d.cost_allocation.total_cost || 0).toFixed(2)} €</td>
                    <td className="py-2 px-2"></td>
                    <td className="py-2 px-2 text-right" data-testid="edlview-tenant-total">{Number(d.cost_allocation.tenant_total || 0).toFixed(2)} €</td>
                    <td className="py-2 px-2 text-right" data-testid="edlview-owner-total">{Number(d.cost_allocation.owner_total || 0).toFixed(2)} €</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700/80">Part restant à la charge du locataire</p>
                <p className="text-lg font-semibold text-amber-800">{Number(d.cost_allocation.tenant_total || 0).toFixed(2)} €</p>
              </div>
              <div className="rounded-2xl bg-brand-lime/15 border border-brand-lime p-3">
                <p className="text-xs text-brand-green/60">Part à la charge du propriétaire (vétusté)</p>
                <p className="text-lg font-semibold text-brand-greenDeep">{Number(d.cost_allocation.owner_total || 0).toFixed(2)} €</p>
              </div>
            </div>
          </div>
        )}

        <EdlDispute mode="public" token={token} authorName={d.tenant_name} />
      </div>
      {signParty && (
        <SignModal token={token} party={signParty} defaultName={signParty === "tenant" ? d.tenant_name : signParty === "owner" ? d.owner_name : d.referent_name} onClose={() => setSignParty(null)} onDone={(res) => { const p = signParty; setSignParty(null); load(); if (p === "tenant" && res) setAck(res); }} />
      )}
      {corrParty && (
        <CorrectionModal token={token} defaultParty={corrParty} onClose={() => setCorrParty(null)} onDone={() => { setCorrParty(null); load(); }} />
      )}
      {ack && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAck(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()} data-testid="sign-ack-modal">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h3 className="font-display text-lg font-semibold text-brand-greenDeep mt-2">Signature enregistrée !</h3>
            <p className="text-sm text-brand-green/60 mt-1">Votre accusé vous a été envoyé par e-mail. Vous pouvez aussi le télécharger :</p>
            <div className="mt-4 flex flex-col gap-2">
              <a href={`${BACKEND}${ack.pdf_url}`} className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="ack-pdf-btn"><FileText className="w-4 h-4" /> Accusé signé (PDF)</a>
              <a href={`${BACKEND}${ack.ics_url}`} className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full border border-brand-soft text-brand-greenDeep text-sm font-semibold" data-testid="ack-ics-btn"><CalendarDays className="w-4 h-4" /> Ajouter à mon agenda (.ics)</a>
            </div>
            <button onClick={() => setAck(null)} className="mt-3 text-xs text-brand-green/50">Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
